import {
  Button,
  Form,
  Table,
  Typography,
  FormInstance,
  Space,
  notification,
  Modal,
  Divider,
  Statistic,
  Alert,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import {
  DirectSale,
  DirectSaleFormValues,
  DirectSaleStatusEnum,
  DirectSaleStockItem,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import DirectSaleAddPartModal from '../DirectSaleAddPartModal';
import {
  useCreateSaleItem,
  useDeleteSaleItem,
  useUpdateSaleItem,
} from '@/core/domain/sales/SaleItem/SaleItem.hooks';
import { getTableScrollX } from '@/utils/table';
import useDisclosure from './../../../../hooks/useDisclosure';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import Authorities from '@/types/Authority';
import { quantityFormatter } from '@/utils/number';
import { InvoiceDTO } from '@/core/domain/invoice/invoice/Invoice.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import formatterPrice from '@/utils/number/formatterPrice';
import formatterPercent from '@/utils/number/formatterPercent';
import { InvoiceSituation } from '@/core/domain/invoice/invoices/invoices.enums';
import { hasPermissions } from '@/utils/functions/hasPermissions';

type Props = {
  form: FormInstance<DirectSaleFormValues>;
  directSale?: DirectSale;
  invoiceData?: InvoiceDTO[];
  totalItens?: number;
};
const { Title, Text } = Typography;

const DirectSalePartsTable: React.FC<Props> = ({
  form,
  totalItens,
  directSale,
  invoiceData,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const [partSelected, setPartSelected] = useState<DirectSaleStockItem>();

  const [addPartModalVisible, addPartModalAction] = useDisclosure(false, {
    onClose() {
      setPartSelected(undefined);
    },
  });

  const createSaleItem = useCreateSaleItem();
  const updateSaleItem = useUpdateSaleItem();
  const removeSaleItem = useDeleteSaleItem(directSale?.id ?? 0);

  const isCanceled = directSale?.status === DirectSaleStatusEnum.CANCELED;
  const hasInvoice = invoiceData?.some((a) =>
    [InvoiceSituation.AUTHORIZED, InvoiceSituation.IN_TYPING].includes(
      a.situation,
    ),
  );

  const handleAddPartModalVisible = (): void => {
    const companyId = form.getFieldValue('companyId') as number;
    const customerId = form.getFieldValue('customerId') as number;
    if (companyId && customerId) {
      addPartModalAction.toggle();
    } else {
      notification.error({
        message: intl.formatMessage({
          id: 'directSale.modal.form.open.emptyCompanyAndCustomerError',
        }),
        description: intl.formatMessage({
          id: 'directSale.modal.form.open.emptyCompanyAndCustomerDescription',
        }),
      });
    }
  };

  const handleRemoveItem = (itemId: number) => {
    if (directSale) {
      Modal.confirm({
        title: intl.formatMessage({
          id: 'directSale.notifications.remove.item.confirm.title',
        }),
        content: `${intl.formatMessage({
          id: 'directSale.notifications.remove.item.confirm.content',
        })}`,
        onOk: async () => {
          await removeSaleItem.mutateAsync(itemId);

          history.push(`/app/movements/directSale/${directSale.id}`);

          notification.success({
            message: intl.formatMessage({
              id: 'directSale.notification.remove.item.success.title',
            }),
            description: intl.formatMessage({
              id: 'directSale.notification.remove.item.success.description',
            }),
          });
        },
        okType: 'danger',
      });
    }
  };

  const columns: ColumnsType<DirectSaleStockItem> = [
    useConfigurationColumnsPart({ isSku: true }),
    {
      width: 200,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'directSale.form.domain.items.description',
      }),
      ellipsis: true,
    },
    {
      width: 90,
      dataIndex: 'totalStock',
      title: intl.formatMessage({
        id: 'directSale.form.domain.items.amount',
      }),
      ellipsis: true,
      render: (value) => quantityFormatter(value),
    },
    {
      width: 160,
      dataIndex: 'unitaryValue',
      title: intl.formatMessage({
        id: 'directSale.form.domain.items.unitaryValue',
      }),
      render: (_value, row) => formatterPrice(row.unitaryValue ?? 0),
    },
    {
      width: 160,
      dataIndex: 'discount',
      title: intl.formatMessage({
        id: 'directSale.form.domain.items.discount',
      }),
      render: (value: number) => formatterPercent(value / 100),
    },
    {
      width: 160,
      dataIndex: 'discountCost',
      title: intl.formatMessage({
        id: 'directSale.form.domain.items.discountCost',
      }),

      render: (value) => formatterPrice(value),
    },
    {
      width: 140,
      dataIndex: 'totalValue',
      title: intl.formatMessage({
        id: 'directSale.form.domain.items.totalValue',
      }),
      render: (_value, row) =>
        formatterPrice(
          (row.totalStock ?? 0) * (row.unitaryValue ?? 0) -
            (row.discountCost ?? 0),
        ),
    },
  ];

  return (
    <div>
      <div>
        {hasInvoice && (
          <Alert
            style={{ width: '100%' }}
            message="Alerta de vínculo"
            description="A venda direta em questão possui uma nota gerada. Para habilitar as funções relacionadas aos itens, é necessário que a nota fiscal seja excluída ou cancelada."
            type="warning"
          />
        )}
      </div>

      <div className="page-header" style={{ marginTop: '16px' }}>
        <Title level={3}>
          <FormattedMessage id="directSale.form.domain.items.parts" />
        </Title>

        <div className="actions">
          <Button
            type="primary"
            block
            onClick={handleAddPartModalVisible}
            disabled={
              !hasPermissions([Authorities.MOVIMENTATION_SALE_ITEM_ADD]) ||
              directSale?.generatedFinancial ||
              isCanceled ||
              hasInvoice
            }
          >
            <FormattedMessage id="directSale.form.domain.button.addPart" />
          </Button>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Text type="danger">{form.getFieldError(['items'])}</Text>
      </div>

      <Form.List name="items" initialValue={[]}>
        {(_fields, operation) => {
          const handleSetItemsTable = async (part: DirectSaleStockItem) => {
            if (typeof part.itemIndex === 'number' && !directSale?.id) {
              form.setFields([
                { name: ['items', part.itemIndex], value: part },
              ]);

              notification.success({
                message: intl.formatMessage({
                  id: 'directSale.notification.update.item.success.title',
                }),
                description: intl.formatMessage({
                  id: 'directSale.notification.update.item.success.description',
                }),
              });
            } else if (directSale?.id) {
              const obj = {
                discount: part.discount / 100,
                discountCost: part.discountCost / 100,
                partId: part.partId,
                quantity: part.totalStock,
                saleId: directSale.id,
                unitCost: part.unitCost,
                unitPrice: part.unitaryValue,
                manufacturerId: part?.manufacturerId,
              };
              if (part.id) {
                await updateSaleItem.mutateAsync({ ...obj, id: part.id });
              } else {
                await createSaleItem.mutateAsync(obj);
              }
            } else {
              operation.add(part);

              notification.success({
                message: 'Sucesso!',
                description: 'Item adicionado com sucesso!',
              });
            }
          };

          columns.push({
            key: 'actions',
            width: 100,
            fixed: 'right',
            align: 'right',
            title: intl.formatMessage({
              id: 'commons.list.header.action',
            }),
            render: (_value, row, itemIndex) => (
              <Space>
                <EditIcon
                  iconProps={{
                    onClick: () => {
                      setPartSelected({ ...row, itemIndex });
                      handleAddPartModalVisible();
                    },
                  }}
                  disabled={
                    !hasPermissions([
                      Authorities.MOVIMENTATION_SALE_ITEM_EDIT,
                    ]) ||
                    directSale?.generatedFinancial ||
                    isCanceled ||
                    hasInvoice
                  }
                />

                <Divider type="vertical" />

                <DeleteIcon
                  onClick={
                    !directSale
                      ? () => {
                          Modal.confirm({
                            title: intl.formatMessage({
                              id: 'directSale.notifications.remove.item.confirm.title',
                            }),
                            content: `${intl.formatMessage({
                              id: 'directSale.notifications.remove.item.confirm.content',
                            })}`,
                            onOk: () => {
                              operation.remove(itemIndex);

                              notification.success({
                                message: intl.formatMessage({
                                  id: 'directSale.notification.remove.item.success.title',
                                }),
                                description: intl.formatMessage({
                                  id: 'directSale.notification.remove.item.success.description',
                                }),
                              });
                            },
                            okType: 'danger',
                          });
                        }
                      : () => handleRemoveItem(row.id)
                  }
                  disabled={
                    !hasPermissions([
                      Authorities.MOVIMENTATION_SALE_ITEM_DELETE,
                    ]) ||
                    directSale?.generatedFinancial ||
                    isCanceled ||
                    hasInvoice
                  }
                />
              </Space>
            ),
          });

          return (
            <>
              <Table
                size="middle"
                dataSource={form.getFieldValue('items')}
                columns={columns}
                rowKey={(_row, index) => String(index)}
                pagination={false}
                scroll={{ x: getTableScrollX(columns) }}
                footer={() => (
                  <Statistic
                    formatter={(value) => formatterPrice(Number(value))}
                    title="Valor total"
                    value={totalItens}
                    style={{ textAlign: 'right' }}
                    valueStyle={{ color: '#a0ce33' }}
                  />
                )}
              />

              <DirectSaleAddPartModal
                visible={addPartModalVisible}
                companyId={form.getFieldValue('companyId')}
                onFinish={handleSetItemsTable}
                directSaleItem={partSelected}
                handleVisible={addPartModalAction.close}
                partsSelected={form.getFieldValue('items')}
                invoiceData={invoiceData}
              />
            </>
          );
        }}
      </Form.List>
    </div>
  );
};

export default DirectSalePartsTable;
